import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Directive, EventEmitter, Inject, Output, ViewChild } from '@angular/core';
import { DomService } from '@portal-core/general/services/dom.service';
import { PluginViewBase } from '@portal-core/text-editor/plugin-views/util/plugin-view.base';
import { PopupTriggerDirective } from '@portal-core/ui/popup/directives/popup-trigger/popup-trigger.directive';

/**
 * The base class for toolbar popup plugin view components (e.g. the equation and node/snippet
 * toolbars) - handles the popup's position/open/close plumbing and the edit/remove events shared
 * by every toolbar popup. Subclasses add their own inputs/outputs and template.
 */
@Directive()
export abstract class ToolbarPopupPluginViewComponentBase extends PluginViewBase {
  @Output() editNode: EventEmitter<void> = new EventEmitter<void>();
  @Output() removeNode: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('popupTrigger', { static: true, read: PopupTriggerDirective }) popupTrigger: PopupTriggerDirective;

  popupPosition: { left: string, top: string } = { left: '0px', top: '0px' };

  get popupOpen(): boolean {
    return this.popupTrigger?.opened ?? false;
  }

  constructor(changeDetectorRef: ChangeDetectorRef, protected domService: DomService, @Inject(DOCUMENT) protected document: Document) {
    super(changeDetectorRef);
  }

  onEditNode() {
    this.editNode.emit();
  }

  onRemoveNode() {
    this.removeNode.emit();
  }

  openPopup(x: number, y: number) {
    // The x/y coordinates are relative to the viewport, so we need to offset them by the overlay container's position
    const overlayPos = this.domService.offsetFrom(this.editorOverlayContainer, this.document.body);
    x -= overlayPos.left;
    y -= overlayPos.top;

    // Only open the popup if its not open or the position has changed
    if (this.popupTrigger && (!this.popupTrigger.opened || this.popupPosition.left !== `${x}px` || this.popupPosition.top !== `${y}px`)) {
      this.popupPosition = { left: `${x}px`, top: `${y}px` };
      this.changeDetectorRef.detectChanges();

      this.popupTrigger.updatePosition();
      this.popupTrigger.open();
      this.changeDetectorRef.detectChanges();
    }
  }

  closePopup() {
    if (this.popupOpen) {
      this.popupTrigger?.close();
      this.changeDetectorRef.detectChanges();
    }
  }
}
